<template>
  <AIVDialog
    :visible="visible"
    title="Future Authorizations Found"
    width="90%"
    height="85vh"
    class="attach-future-auth-modal"
    @update:visible="$emit('update:visible', $event)"
  >
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3">Checking for Future authorizations...</p>
    </div>

    <div v-else-if="futureAuths.length > 0">
      <div class="future-auth-notice" role="alert">
        <strong>Future authorizations were found for this client and service code.</strong>
        <br />
        These were created when shift hours or units exceeded the available authorization.
        Would you like to attach them to the newly created authorization?
      </div>

      <div class="future-auth-list mt-4">
        <AIVTable
          ref="aivTable"
          :columns="tableColumns"
          :data="futureAuths"
          :pagination="{ total: futureAuths.length, totalPages: 1, currentPage: 1, perPage: futureAuths.length }"
          item-name="future authorizations"
        >
          <template #header>
            <tr>
              <th>
                <input
                  type="checkbox"
                  :checked="selectedAuthIds.length === futureAuths.length && futureAuths.length > 0"
                  @change="toggleSelectAll"
                />
              </th>
              <th
                v-for="column in tableColumns.slice(1)"
                :key="column.key"
                @click="column.sortable !== false ? sortBy(column.key) : null"
                :class="{
                  'sortable': column.sortable !== false,
                }"
              >
                {{ column.label }}
              </th>
            </tr>
          </template>
          <template #cell-select="{ row }">
            <input
              type="checkbox"
              :value="row.id"
              v-model="selectedAuthIds"
            />
          </template>
          <template #cell-client="{ row }">
            {{ getClientDisplay(row) }}
          </template>
          <template #cell-service_code="{ row }">
            {{ getServiceCodeDisplay(row) }}
          </template>
          <template #cell-hours_used="{ row }">
            {{ formatUsage(usageValue(row)) }}
          </template>
          <template #cell-createdAt="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
          <template #cell-auth_description="{ row }">
            <span
              v-if="row.auth_description"
              :title="row.auth_description"
              class="description-cell"
            >
              {{ row.auth_description.length > 50
                ? row.auth_description.substring(0, 50) + '...'
                : row.auth_description }}
            </span>
            <span v-else class="text-muted">No description</span>
          </template>
        </AIVTable>

        <div v-if="selectedAuthIds.length > 0" class="selection-summary mt-3">
          <div class="row">
            <div class="col-md-6">
              <strong>Selected Authorizations:</strong>
              <span class="ml-2">{{ selectedAuthIds.length }} of {{ futureAuths.length }}</span>
            </div>
            <div class="col-md-6">
              <strong>{{ isUnitBased ? 'Total Units to Transfer:' : 'Total Hours to Transfer:' }}</strong>
              <span class="ml-2 text-primary font-weight-bold">{{ formatUsage(totalSelectedUsage) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <p class="text-muted">No Future authorizations found</p>
    </div>

    <template slot="footer">
      <div class="dialog-footer">
        <button class="btn btn-secondary" @click="handleCancel">
          {{ futureAuths.length > 0 ? 'Skip' : 'Close' }}
        </button>
        <button
          v-if="futureAuths.length > 0"
          class="btn btn-primary"
          :disabled="selectedAuthIds.length === 0 || attaching"
          @click="handleAttach"
        >
          <span v-if="attaching">
            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
            Attaching...
          </span>
          <span v-else>
            Attach {{ selectedAuthIds.length }} Selected
          </span>
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from './AIVDialog.vue';
import AIVTable from './AIVTable.vue';
import { authorizationsService } from 'src/serviceHandlers/authorizationsService';

export default {
  name: 'AttachFutureAuthModal',
  components: {
    AIVDialog,
    AIVTable,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    clientId: {
      type: Number,
      default: null,
    },
    serviceCodeId: {
      type: Number,
      default: null,
    },
    authorizationId: {
      type: Number,
      default: null,
    },
    payerType: {
      type: String,
      default: 'VR',
    },
  },
  data() {
    return {
      futureAuths: [],
      selectedAuthIds: [],
      loading: false,
      attaching: false,
    };
  },
  computed: {
    isUnitBased() {
      // DSPD and PP budgets track units; VR auths track hours
      return this.payerType === 'DSPD' || this.payerType === 'PP';
    },
    totalSelectedUsage() {
      return this.futureAuths
        .filter(auth => this.selectedAuthIds.includes(auth.id))
        .reduce((sum, auth) => sum + (parseFloat(this.usageValue(auth)) || 0), 0);
    },
    tableColumns() {
      return [
        {
          key: 'select',
          label: '',
          sortable: false,
        },
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'client',
          label: 'Client',
          sortable: true,
        },
        {
          key: 'service_code',
          label: 'Service Code',
          sortable: true,
        },
        {
          key: 'hours_used',
          label: this.isUnitBased ? 'Units Used' : 'Hours Used',
          sortable: true,
        },
        {
          key: 'createdAt',
          label: 'Created Date',
          sortable: true,
        },
        {
          key: 'auth_description',
          label: 'Description',
          sortable: false,
        },
      ];
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadFutureAuthorizations();
      } else {
        // Reset state when modal closes
        this.futureAuths = [];
        this.selectedAuthIds = [];
      }
    },
  },
  methods: {
    async loadFutureAuthorizations() {
      if (!this.clientId || !this.visible) return;

      this.loading = true;
      try {
        const response = await authorizationsService.getFutureAuthorizations(
          this.clientId,
          this.serviceCodeId
        );
        this.futureAuths = response || []; // response is already the data

        // Auto-select all by default
        if (this.futureAuths.length > 0) {
          this.selectedAuthIds = this.futureAuths.map(auth => auth.id);
        }
      } catch (error) {
        this.$notifyError('Failed to load Future authorizations');
        this.futureAuths = [];
      } finally {
        this.loading = false;
      }
    },

    getClientDisplay(auth) {
      // Try to get the client name from various possible structures
      if (auth.client && auth.client.name) {
        return auth.client.name;
      } else if (auth.client_name) {
        return auth.client_name;
      } else if (auth.client && auth.client.first_name && auth.client.last_name) {
        return `${auth.client.first_name} ${auth.client.last_name}`;
      }
      return 'N/A';
    },

    getServiceCodeDisplay(auth) {
      // Try to get the short code first, fall back to the name if needed
      if (auth.service_code && auth.service_code.code) {
        return auth.service_code.code;
      } else if (auth.service_code_name) {
        // If we only have the name, try to extract a short version or use as-is
        return auth.service_code_name;
      }
      return 'N/A';
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedAuthIds = this.futureAuths.map(auth => auth.id);
      } else {
        this.selectedAuthIds = [];
      }
    },

    sortBy(key) {
      // Let AIVTable handle the sorting
      this.$refs.aivTable?.sortBy(key);
    },

    handleCancel() {
      this.$emit('update:visible', false);
    },

    async handleAttach() {
      if (this.selectedAuthIds.length === 0) {
        this.$notifyWarning('Please select at least one Future authorization to attach');
        return;
      }

      this.attaching = true;
      try {
        const response = await authorizationsService.attachFutureAuthorizations(
          this.authorizationId,
          this.selectedAuthIds
        );

        if (response.success) {
          const transferred = this.isUnitBased
            ? `${response.unitsTransferred} unit(s)`
            : `${response.hoursTransferred} hours`;
          this.$notifySuccess(
            `Successfully attached ${transferred} from ${this.selectedAuthIds.length} Future authorization(s)`
          );
          this.$emit('attached', response);
          this.$emit('update:visible', false);
        } else {
          throw new Error(response.message || 'Failed to attach Future authorizations');
        }
      } catch (error) {
        this.$notifyError(error.message || 'Failed to attach Future authorizations');
      } finally {
        this.attaching = false;
      }
    },

    // Sentinel usage lives in units_used for DSPD/PP and hours_used for VR
    usageValue(row) {
      return this.isUnitBased ? row.units_used : row.hours_used;
    },

    formatUsage(value) {
      const v = parseFloat(value) || 0;
      return this.isUnitBased ? String(Math.round(v)) : v.toFixed(2);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
/* Overall modal font size increase */
.aiv-dialog :deep(.dialog-content) {
  font-size: 16px;
}

.future-auth-list {
  max-height: 400px;
  overflow-y: auto;
  margin-top: 1rem;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 1rem;
}

/* Future auth notice - consistent with app styling, no light blue */
.future-auth-notice {
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(66, 98, 116, 0.5);
  border-radius: 6px;
  background-color: rgba(37, 55, 65, 0.4);
  color: #e0e0e0;
  font-size: 16px;
  line-height: 1.5;
}

/* Selection summary styling */
.selection-summary {
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid rgba(66, 98, 116, 0.3);
  border-radius: 6px;
  background-color: rgba(37, 55, 65, 0.3);
  color: #e0e0e0;
  font-size: 16px;
}

/* Description cell with hover tooltip */
.description-cell {
  cursor: help;
}

/* Table font size override - consistent 16px */
.future-auth-list :deep(.aiv-table) {
  font-size: 16px;
}

.future-auth-list :deep(.aiv-table td) {
  font-size: 16px;
}

.future-auth-list :deep(.aiv-table th) {
  font-size: 16px;
}

/* Loading state styling */
.spinner-border {
  width: 2rem;
  height: 2rem;
  border: 0.25em solid rgba(255, 255, 255, 0.2);
  border-right-color: #e0e0e0;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Checkbox styling */
input[type="checkbox"] {
  background-color: #656565;
  border: 1px solid #bdbdbd;
  border-radius: 3px;
  width: 16px;
  height: 16px;
  accent-color: #ffffff;
}

input[type="checkbox"]:checked {
  background-color: #4CAF50;
  border-color: #4CAF50;
}

/* Button styling to match app theme */
.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}

.btn-primary:disabled {
  background-color: rgba(0, 123, 255, 0.5);
  border-color: rgba(0, 123, 255, 0.5);
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: transparent;
  border-color: rgba(255, 255, 255, 0.3);
  color: #e0e0e0;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
  color: #ffffff;
}

/* Spinner styling for buttons */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

/* Text colors */
.text-primary {
  color: #17a2b8 !important;
}

.text-muted {
  color: #999999 !important;
}

.font-weight-bold {
  font-weight: 600 !important;
}

/* Row styling */
.row {
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}

.col-md-6 {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  flex: 0 0 50%;
  max-width: 50%;
}

@media (max-width: 767.98px) {
  .col-md-6 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 0.5rem;
  }
}

/* Loading and empty state styling */
.text-center {
  text-align: center;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}
</style>